/**
 * Lead outreach status: Active, Bounces, Unsubscribes.
 */
import React from 'react';
import { Tag } from 'antd';
import type { Lead } from '../types';

export type LeadStatusFilter = 'active' | 'bounces' | 'unsubscribes';

/** Ant Design Tag colors for lead status */
export const BOUNCE_TAG_COLOR = 'gold';
export const UNSUBSCRIBE_TAG_COLOR = 'red';

export function isBounceLead(lead: Pick<Lead, 'is_suppressed' | 'suppression_reason'>): boolean {
  return Boolean(lead.is_suppressed && lead.suppression_reason === 'bounce');
}

export function isUnsubscribeLead(
  lead: Pick<Lead, 'do_not_send' | 'is_suppressed' | 'suppression_reason'>,
): boolean {
  if (isBounceLead(lead)) return false;
  return Boolean(lead.do_not_send) || lead.suppression_reason === 'unsubscribe';
}

export function leadToStatusFilter(
  lead: Pick<Lead, 'is_suppressed' | 'suppression_reason' | 'do_not_send'>,
): LeadStatusFilter {
  if (isBounceLead(lead)) return 'bounces';
  if (isUnsubscribeLead(lead)) return 'unsubscribes';
  return 'active';
}

export function getLeadStatusLabel(lead: Pick<Lead, 'is_suppressed' | 'suppression_reason' | 'do_not_send'>): string {
  if (isBounceLead(lead)) return 'Bounces';
  if (isUnsubscribeLead(lead)) return 'Unsubscribes';
  return 'Active';
}

export function renderLeadStatusTag(lead: Pick<Lead, 'is_suppressed' | 'suppression_reason' | 'do_not_send'>): React.ReactNode {
  if (isBounceLead(lead)) {
    return <Tag color={BOUNCE_TAG_COLOR}>Bounces</Tag>;
  }
  if (isUnsubscribeLead(lead)) {
    return <Tag color={UNSUBSCRIBE_TAG_COLOR}>Unsubscribes</Tag>;
  }
  return <Tag color="green">Active</Tag>;
}
